import {createStyles, Text} from '@mantine/core';
import React from 'react';

import getFieldIcon from 'analysis-tool/structured-data-mapping/column-mapping-table/getFieldIcon';
import {ScratchPlatePlateFieldMapping} from 'plates/dao/ScratchPlateDAO';
import {CommonPlateField} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import ScratchPlatePlateFieldSelector, {
  decodePlateFieldValue,
  encodePlateFieldValue,
} from 'samples/plate-maps/scratch-plates/ScratchPlatePlateFieldSelector';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface ScratchPlatePlateFieldMappingTableProps {
  /** Target plate-schema fields that can be set (the mapping destinations). */
  schemaFields: Array<SchemaField>;
  /** Ad hoc plate-level metadata fields present on any schema-less plate being promoted (see getUnionPlateFields). */
  metadataFields: Array<CommonPlateField>;
  mappings: Array<ScratchPlatePlateFieldMapping>;
  setMappings: (mappings: Array<ScratchPlatePlateFieldMapping>) => void;
}

function updateMappingsOnSelect(
  mappings: Array<ScratchPlatePlateFieldMapping>,
  schemaFieldName: string,
  token: string | null
): Array<ScratchPlatePlateFieldMapping> {
  const withoutField = mappings.filter((mapping) => mapping.schemaFieldName !== schemaFieldName);
  if (token == null) {
    return withoutField;
  }
  const {name: metadataFieldName, type: metadataFieldType} = decodePlateFieldValue(token);
  return [...withoutField, {metadataFieldName, metadataFieldType, schemaFieldName}];
}

/**
 * Encodes the mapping's stored (name, type) pair back into a selector token. Both are stored on
 * ScratchPlatePlateFieldMapping precisely so a mapping created while a name collided across types can
 * still disambiguate which type-variant was originally selected.
 */
function encodeSelectedValue(mapping: ScratchPlatePlateFieldMapping | undefined): string | null {
  if (mapping == null) {
    return null;
  }
  return encodePlateFieldValue(mapping.metadataFieldName, mapping.metadataFieldType);
}

/**
 * Destination-centric plate-level field mapping, mirroring ScratchPlateColumnMappingTable: one row
 * per target plate-schema field, paired with a dropdown of the ad hoc metadata fields present on any
 * schema-less plate being promoted (see getUnionPlateFields) — a plate lacking a given field name
 * just gets a null value for the field it's mapped to. Unlike column mapping, there's no per-layer
 * grouping at the plate level (plate fields aren't scoped to a layer the way well columns are), so
 * ScratchPlatePlateFieldSelector — a flat sibling of ScratchPlateSourceColumnSelector built on the
 * same underlying components — is used instead, for identical styling (type icons, Chip display)
 * without the layer header rows.
 *
 * Every row offers the full set of metadata fields, unfiltered by type, matching Column mapping's
 * unfiltered behavior. A field name can collide across more than one type across the batch (see
 * getUnionPlateFields), so options are labeled with their type (see getCommonPlateFieldDisplayLabel)
 * whenever that happens, so the user can tell which type-variant of a name they're choosing.
 */
export default function ScratchPlatePlateFieldMappingTable({
  schemaFields,
  metadataFields,
  mappings,
  setMappings,
}: ScratchPlatePlateFieldMappingTableProps): JSX.Element {
  const {classes} = useStyles();

  const mappingByField = Object.fromEntries(mappings.map((mapping) => [mapping.schemaFieldName, mapping]));

  if (schemaFields.length === 0) {
    return <Text c="dimmed">This schema does not have any fields to map.</Text>;
  }

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Plate schema field</th>
          <th>Metadata field</th>
        </tr>
      </thead>
      <tbody>
        {schemaFields.map((schemaField) => (
          <tr key={schemaField.systemName}>
            <td className={classes.fieldCell}>
              <BenchlingIcon className={classes.typeIcon} icon={getFieldIcon(schemaField)} />
              {schemaField.displayName}
            </td>
            <td className={classes.selectorCell}>
              <ScratchPlatePlateFieldSelector
                fields={metadataFields}
                selectedValue={encodeSelectedValue(mappingByField[schemaField.systemName])}
                onChange={(token): void => {
                  setMappings(updateMappingsOnSelect(mappings, schemaField.systemName, token));
                }}
                allowSelectNull={true}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const useStyles = createStyles((theme) => ({
  fieldCell: {
    verticalAlign: 'middle',
  },
  typeIcon: {
    color: theme.colors.gray[6],
    marginRight: theme.spacing.xs,
  },
  selectorCell: {
    width: 255,
  },
}));
